/**
 * Rock Off Retail — "Recently Viewed" rail (17 Aug hub-restructure, Sohail
 * spec item 4: "a 'Recently viewed' row rendered client-side from the
 * rockoff-affinity.js localStorage products list ... render with the
 * existing .ro-card pattern; hidden when empty, no shell").
 *
 * window.ROAffinity.get().products is already populated site-wide (every
 * PDP calls recordProductView via the data-ro-pdp-product hook — see
 * assets/rockoff-affinity.js autoTrack()), newest-viewed first, capped 24.
 * This file just resolves each stored handle to real product data via the
 * storefront's own /products/<handle>.js AJAX endpoint (no app/API needed,
 * works behind the storefront password wall the same way the affinity
 * script itself does) and renders cards with the exact same .ro-card
 * markup/classes as sections/rockoff-product-slider.liquid and
 * sections/rockoff-for-you.liquid (same section-rockoff-product-slider.css,
 * reused unchanged here).
 *
 * CONSENT: the view history is passive browsing data. Nothing is read,
 * requested or rendered unless ROConsent.allowed('personalisation') is true
 * (waits for ROConsent.onReady; ROConsent absent -> fail closed). Withdrawal
 * (ROConsent.onChange) clears and hides the rail; acceptance may render it.
 *
 * Renders nothing (section stays [hidden]) when there is no view history
 * yet, or when every stored handle 404s (deleted/unpublished product) —
 * never an empty heading / broken shell.
 */
(function () {
  'use strict';

  function formatPrice(cents) {
    var n = parseFloat(cents) / 100;
    if (isNaN(n)) return '';
    // Rock Off Retail UK is GBP-only at this stage — same assumption
    // rockoff-for-you.js makes.
    return '£' + n.toFixed(2);
  }

  function flagFor(product) {
    var tags = Array.isArray(product.tags) ? product.tags : [];
    var hasVault = tags.some(function (t) {
      return String(t).toLowerCase() === 'vault';
    });
    if (hasVault) return 'VAULT';
    var hasNew = tags.some(function (t) {
      return String(t).toLowerCase() === 'new';
    });
    if (hasNew) return 'NEW';
    return '';
  }

  // -------------------------------------------------------------------
  // Design-dedup (18 Aug, Sohail ruling; algorithm corrected TWICE the
  // same day — see assets/rockoff-for-you.js's designDedupeKey() comment
  // for the full bugfix writeup, incl. live verification numbers) — one
  // product per DESIGN in this rail, identical logic to that file's
  // designDedupeKey() (duplicated, not shared — plain script tags, no
  // module system). Unlike rockoff-for-you.js, this file fetches full
  // /products/<handle>.js payloads, which DO include a real `variants`
  // array with `sku` — so the SKU branch is live-capable here the moment
  // a genuinely non-DEMO Spiral SKU exists, not just a dormant fallback.
  // -------------------------------------------------------------------
  var GARMENT_KEYWORDS = [
    'smock',
    'tunic',
    'hoody',
    'hoodie',
    'bag',
    'tote',
    'backpack',
    'sleepsuit',
    'robe',
    'pyjama',
    'workshirt',
    't-shirt',
    'tshirt',
    'cap',
    'beanie',
    'sock',
    'mug',
    'dress',
    'vest',
    'zip sleeve',
  ];

  function isGarmentSegment(seg) {
    var low = (seg || '').trim().toLowerCase();
    for (var i = 0; i < GARMENT_KEYWORDS.length; i++) {
      if (low.indexOf(GARMENT_KEYWORDS[i]) !== -1) return true;
    }
    return false;
  }

  function designDedupeKey(product) {
    var sku = (product.variants && product.variants[0] && product.variants[0].sku) || '';
    var skuUp = String(sku).toUpperCase();
    var isSpiralSku = false;
    if (skuUp.length >= 4 && skuUp.indexOf('-') === -1) {
      if (/^[A-Z][0-9]{3}/.test(skuUp)) {
        isSpiralSku = true;
      }
    }
    if (isSpiralSku) {
      return 'sku:' + skuUp;
    }

    var title = product.title || '';
    var parts = title.split(' - ');

    var designPart;
    if (parts.length >= 3 && !isGarmentSegment(parts[1])) {
      designPart = parts[0] + ' - ' + parts[1];
    } else {
      designPart = parts[0];
    }
    return 'title:' + designPart.trim().toLowerCase();
  }

  // -------------------------------------------------------------------
  // Quick-add icon + modal (19 Aug post-demo) — identical rationale and
  // markup to assets/rockoff-for-you.js's buildQuickAdd (duplicated, not
  // shared — plain script tags, no module system; see that file's comment
  // for the full writeup, incl. why .ro-card-wrap is also the fix for the
  // variable-width cards). Host section rockoff-recently-viewed.liquid
  // loads quick-add.css/quick-add.js/product-form.js.
  // -------------------------------------------------------------------
  var CART_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart" viewBox="0 0 40 40"><path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 0 0-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0 0 20.5 6.5m3.75 5.31v-.56a3.75 3.75 0 1 0-7.5 0v.56zm-7.5 1h7.5v.56a3.75 3.75 0 1 1-7.5 0zm-1 0v.56a4.75 4.75 0 1 0 9.5 0v-.56h2.22l.71 10.67a4 4 0 0 1-3.99 4.27h-7.38a4 4 0 0 1-4-4.27l.72-10.67z"/></svg>';
  var CLOSE_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';
  var SPINNER_SVG =
    '<div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';

  function escapeAttr(str) {
    return String(str == null ? '' : str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  var quickaddLabels = { add: 'Choose options', close: 'Close' };

  // Locale-aware URL root + rail-specific "rv" id prefix — same two
  // checker findings as rockoff-for-you.js (see comments there): the
  // modal fetch must carry the locale root or DE/FR visitors get an
  // English modal, and the same product in both homepage rails must not
  // mint duplicate DOM ids.
  function localeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function localeUrl(path) {
    return localeRoot() + String(path || '').replace(/^\//, '');
  }

  function buildQuickAdd(productId, productUrl) {
    var div = document.createElement('div');
    div.className = 'ro-card-quickadd';
    var id = 'QuickAdd-rv' + escapeAttr(productId);
    // Inert <template> + ModalClose- id — same two-part crash fix as
    // rockoff-for-you.js's buildQuickAdd; see the comment there for the
    // full live-debug trace.
    var tpl = document.createElement('template');
    tpl.innerHTML =
      '<modal-opener data-modal="#' + id + '">' +
      '<button type="button" class="ro-card-quickadd__btn" aria-haspopup="dialog" title="' + escapeAttr(quickaddLabels.add) + '" data-product-url="' + escapeAttr(productUrl) + '">' +
      '<span class="visually-hidden">' + escapeAttr(quickaddLabels.add) + '</span>' +
      '<span class="ro-card-quickadd__glyph" aria-hidden="true">' + CART_ICON_SVG + '</span>' +
      SPINNER_SVG +
      '</button>' +
      '</modal-opener>' +
      '<quick-add-modal id="' + id + '" class="quick-add-modal">' +
      '<div role="dialog" aria-label="' + escapeAttr(quickaddLabels.add) + '" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">' +
      '<button id="ModalClose-rv' + escapeAttr(productId) + '" type="button" class="quick-add-modal__toggle" aria-label="' + escapeAttr(quickaddLabels.close) + '">' + CLOSE_ICON_SVG + '</button>' +
      '<div id="QuickAddInfo-rv' + escapeAttr(productId) + '" class="quick-add-modal__content-info"></div>' +
      '</div>' +
      '</quick-add-modal>';
    div.appendChild(tpl.content);
    return div;
  }

  // 24 Sep, Sohail item 6: skip the WHOLE card when there is no image — see
  // assets/rockoff-for-you.js's buildCard() comment for the full rationale
  // (same fix, duplicated for the same reason as the rest of this file).
  function buildCard(product) {
    var imgSrc = product && (product.featured_image || (product.images && product.images[0]));
    if (!imgSrc) return null;

    var a = document.createElement('a');
    a.className = 'ro-card';
    a.href = localeUrl(product.url);

    var well = document.createElement('span');
    well.className = 'ro-card__well';

    var flag = flagFor(product);
    if (flag) {
      var flagEl = document.createElement('span');
      flagEl.className = 'ro-card__flag';
      flagEl.textContent = flag;
      well.appendChild(flagEl);
    }

    var img = document.createElement('img');
    img.className = 'ro-card__img ro-card__img--primary';
    img.src = imgSrc + (imgSrc.indexOf('?') === -1 ? '?width=500' : '&width=500');
    img.alt = product.title || '';
    img.loading = 'lazy';
    well.appendChild(img);

    a.appendChild(well);

    var title = document.createElement('h4');
    title.className = 'ro-card__title';
    title.textContent = product.title || '';
    a.appendChild(title);

    var bandName = product.vendor;
    if (bandName) {
      var band = document.createElement('span');
      band.className = 'ro-card__band';
      band.textContent = bandName;
      a.appendChild(band);
    }

    var priceEl = document.createElement('div');
    priceEl.className = 'ro-card__price';
    priceEl.textContent = formatPrice(product.price);
    a.appendChild(priceEl);

    // .ro-card-wrap carries the slider-item sizing and anchors the
    // quick-add overlay — see buildQuickAdd comment above.
    var wrap = document.createElement('div');
    wrap.className = 'ro-card-wrap';
    wrap.appendChild(a);
    wrap.appendChild(buildQuickAdd(product.id || product.handle, localeUrl(product.url)));
    return wrap;
  }

  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Consent gate (29 Sep 2026, cookie Option A fix round 2). The viewed-
  // products list is PASSIVE browsing history: it may be read, requested from
  // the storefront and rendered ONLY while ROConsent.allowed('personalisation')
  // is true at that moment. Fail closed: ROConsent absent -> nothing.
  // A per-section render token makes stale async results harmless: every
  // render()/clear() bumps it, and a fetch result is discarded unless its
  // token is still current AND consent is still on.
  function consented() {
    try {
      return !!(window.ROConsent && typeof window.ROConsent.allowed === 'function' && window.ROConsent.allowed('personalisation') === true);
    } catch (err) {
      return false;
    }
  }

  function initSection(section) {
    var productsEl = section.querySelector('[data-ro-recently-viewed-products]');
    if (!productsEl) return;
    var consent = window.ROConsent;
    // Fail closed: no consent seam -> never read, request or render history.
    if (!consent || typeof consent.onReady !== 'function') return;

    var maxProducts = parseInt(section.getAttribute('data-ro-products-to-show'), 10) || 8;
    // Locale-correct quick-add labels from the section's Liquid.
    quickaddLabels.add = section.getAttribute('data-ro-quickadd-label') || quickaddLabels.add;
    quickaddLabels.close = section.getAttribute('data-ro-close-label') || quickaddLabels.close;

    var token = 0;
    // True while a consented render's fetches are in flight. render() is
    // idempotent meanwhile (a second call for the same state is ignored), so
    // accepting on the page - which fires onReady AND onChange for the same
    // event - fetches each handle once. clear() resets it (withdrawal).
    var inFlight = false;
    // Last personalisation-consent state seen; onChange ignores events that
    // did not change it (e.g. an analytics toggle, or the same acceptance
    // that onReady already handled).
    var lastConsent = false;

    function clear() {
      token++; // invalidate any in-flight fetch
      inFlight = false;
      while (productsEl.firstChild) productsEl.removeChild(productsEl.firstChild);
      section.hidden = true;
    }

    function render() {
      if (inFlight && consented()) return; // idempotent while a render is in flight
      var myToken = ++token;
      inFlight = false;
      if (!consented() || !window.ROAffinity) {
        // Consent absent / unknown / withdrawn: nothing shown, nothing read.
        while (productsEl.firstChild) productsEl.removeChild(productsEl.firstChild);
        section.hidden = true;
        return;
      }

      var affinity = window.ROAffinity.get();
      var entries = Array.isArray(affinity && affinity.products) ? affinity.products : [];
      if (!entries.length) return; // no view history at all — stay hidden, zero fetches

      // BUGFIX (18 Aug): this used to slice to `maxProducts` HANDLES before
      // fetching/deduping — so if any of the first `maxProducts` view-history
      // entries were duplicate designs (two garments of the same design
      // viewed back to back), the rail rendered fewer than maxProducts cards
      // even though enough distinct designs existed further down the
      // history to fill it. Fetch every stored handle instead (already
      // capped at PRODUCTS_CAP=24 by assets/rockoff-affinity.js, so still a
      // small, bounded number of AJAX calls) and cut to maxProducts AFTER
      // dedup — same "slice after dedup" pattern as rockoff-for-you.js's
      // interleaveAndDedupe().
      var handles = entries
        .map(function (p) {
          return p && p.handle;
        })
        .filter(Boolean);
      if (!handles.length) return;

      inFlight = true;
      Promise.all(handles.map(fetchProduct)).then(function (products) {
        // Stale (a newer render/clear happened) or consent withdrawn while
        // the requests were in flight: discard, render nothing.
        if (myToken !== token) return;
        inFlight = false;
        if (!consented()) return;

        // Design-dedup (18 Aug): `products` is already in most-recently-
        // viewed-first order (see the `entries`/`handles` mapping above,
        // sourced from assets/rockoff-affinity.js's `data.products`, which
        // recordProductView() keeps newest-first) — so "first occurrence
        // wins" here naturally keeps whichever garment of a given design the
        // visitor looked at most recently, same "first occurrence in an
        // already-meaningfully-sorted list wins" rule as the Liquid sliders.
        var cards = [];
        var seenDesignKeys = {};
        for (var i = 0; i < products.length && cards.length < maxProducts; i++) {
          var product = products[i];
          if (!product) continue;
          var key = designDedupeKey(product);
          if (seenDesignKeys[key]) continue;
          var card = buildCard(product);
          if (!card) continue; // no image — skip, keep scanning remaining view-history entries
          seenDesignKeys[key] = true;
          cards.push(card);
        }

        if (!cards.length) return; // every stored handle came back empty (or fully duplicate) — stay hidden

        while (productsEl.firstChild) productsEl.removeChild(productsEl.firstChild);
        cards.forEach(function (card) {
          productsEl.appendChild(card);
        });

        section.hidden = false;
      }).catch(function () {
        // Any failure in the chain — leave the section hidden, never show a
        // broken/partial rail.
        if (myToken === token) inFlight = false;
      });
    }

    // Wait for the consent API to settle (loaded, or definitively failed ->
    // allowed() false -> stays hidden). Never render before that.
    consent.onReady(function () {
      lastConsent = consented();
      render();
    });
    // Acceptance may render; withdrawal hides and clears at once. Events that
    // leave the personalisation state unchanged are ignored.
    if (typeof consent.onChange === 'function') {
      consent.onChange(function () {
        var c = consented();
        if (c === lastConsent) return;
        lastConsent = c;
        if (c) render();
        else clear();
      });
    }
  }

  function init() {
    var sections = document.querySelectorAll('[data-ro-recently-viewed]');
    sections.forEach(function (section) {
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Test-only export hook (24 Sep, item 6 node harness) — no-op in the
  // browser; see assets/rockoff-for-you.js's matching hook for rationale.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildCard: buildCard, designDedupeKey: designDedupeKey };
  }
})();
